// The player web portal's copy (spec/15 §2), the `player` namespace. Loaded by `apps/player-web`
// alone, through `@clavian/i18n/player-web`, so the console, the kiosk and the store apps never
// carry it.
//
// The voice is BRAND.md §6's: say what happens, plainly; a control says exactly what it does; an
// empty state says what to do next. The words are the glossary's: venue, station, session, pass,
// wallet, player, never "terminal", "machine" or "seat". A PC is what a player calls a station.
// Customer copy carries no em dashes and no internal references.
//
// Money and time are never written as literals here: an amount is integer minor units formatted by
// the venue formatter (`@clavian/i18n` `createVenueFormatter`), and a clock follows the venue's
// timezone. Interpolations named `{{amount}}`, `{{time}}` and the like are filled with those.
export const playerWeb = {
    app: {
        skipToContent: 'Skip to the page',
        loading: 'Loading',
        menu: 'Menu',
    },
    // The signed-in navigation (a bottom tab bar on phones, a side rail on desktop) and the signed-out top bar.
    nav: {
        home: 'Home',
        venues: 'Venues',
        play: 'Play',
        wallet: 'Wallet',
        passes: 'Passes',
        orders: 'Orders',
        account: 'Account',
        signIn: 'Sign in',
        signUp: 'Create account',
        primary: 'Main',
        yourStuff: 'Your money and passes',
        live: 'Live',
        theme: {
            label: 'Appearance',
            system: 'Match device',
            light: 'Light',
            dark: 'Dark',
        },
        signOut: 'Sign out',
    },
    // The signed-out landing: what the site does today, and two ways in.
    landing: {
        eyebrow: 'Your play, your account',
        title: 'Everything you play, in one place.',
        subtitle: 'See the time left on your PC, lock it or add time from your phone, check your wallet and passes, and keep every receipt. At every {{brand}} venue.',
        ctaSignIn: 'Sign in',
        ctaSignUp: 'Create your account',
        ctaBrowse: 'Find a venue',
        features: {
            session: {
                title: 'Your live session',
                body: 'Time left, what is paying for it, and the controls: lock the PC, add time, call staff or log out.',
            },
            wallet: {
                title: 'Wallet and passes',
                body: 'Your balance at each venue, every top-up and spend, and how much time is left on each pass.',
            },
            receipts: {
                title: 'Orders and receipts',
                body: 'Every order you placed at the counter or the PC, with the receipt the venue gave you.',
            },
        },
        payNote: 'Passes and top-ups are bought at the venue counter.',
    },
    signIn: {
        title: 'Sign in',
        intro: 'A code to your phone, or your password.',
        introPassword: 'With your email or gamer tag and your password.',
        footer: 'New to {{brand}}? Create your account in a minute.',
        method: {
            legend: 'How would you like to sign in?',
            phone: 'Phone',
            password: 'Password',
        },
        phone: {
            number: 'Phone number',
            numberHint: 'In full, with the country code, for example +91 98765 43210.',
            send: 'Send me a code',
            sent: 'We sent a code to {{phone}}.',
            code: 'The code',
            codeHint: 'The six digits we sent to {{phone}}.',
            verify: 'Sign in',
            change: 'Use another number',
            resend: 'Send a new code',
            resent: 'A new code is on its way.',
        },
        password: {
            identifier: 'Email or gamer tag',
            password: 'Password',
            submit: 'Sign in',
            forgot: 'Forgot your password?',
        },
        // A second step for an account with two-factor authentication on.
        secondFactor: {
            title: 'Enter your security code',
            intro: 'Open your authenticator app and type the six-digit code for Clavian.',
            code: 'Security code',
            submit: 'Verify and sign in',
            useBackup: 'Use a backup code instead',
            useApp: 'Use the authenticator app instead',
            backupCode: 'Backup code',
            backupHint: 'One of the codes you saved when you turned two-factor on. Each works once.',
            back: 'Back',
        },
        // An email sign-up whose address still needs its code.
        verify: {
            title: 'Check your email',
            intro: 'We sent a six-digit code to {{email}}. Enter it to finish creating your account.',
            code: 'The code',
            submit: 'Verify and continue',
            resend: 'Send a new code',
            resent: 'A new code is on its way.',
            done: 'Your email is verified. Sign in to continue.',
        },
        required: 'Needed',
        invalidPhone: 'That is not a phone number we can send a code to.',
        failure: {
            credentials: 'That did not match. Check it and try again.',
            code: 'That code did not work. Check it, or ask for a fresh one.',
            rateLimited: 'Too many tries. Give it a minute, then try again.',
            banned: 'This account cannot sign in. Contact the venue.',
            notVerified: 'Your email address is not verified yet. We sent a code to it: enter it below.',
            delivery: 'We could not send a code right now. Try again shortly.',
            noAccount: 'No account uses that number yet. Create one first.',
            phoneOff: 'Signing in with a phone number is not available right now. Use your email or gamer tag and password.',
        },
        newHere: 'New here? Create your account',
    },
    // Forgot password: a code by email, then a new password.
    forgot: {
        title: 'Reset your password',
        intro: 'Enter the email on your account and we will send you a code.',
        email: 'Email',
        send: 'Send me a code',
        sentIntro: 'If {{email}} has an account, a six-digit code is on its way. It works for a few minutes.',
        code: 'The code',
        password: 'New password',
        passwordHint: 'At least 8 characters. Avoid one you use anywhere else.',
        submit: 'Set new password',
        resend: 'Send a new code',
        changeEmail: 'Use another email',
        done: 'Your password is changed. Sign in with it now.',
        doneSignedIn: 'Your password is changed.',
        signIn: 'Sign in',
        back: 'Back to sign in',
        backAccount: 'Back to your account',
        failure: {
            code: 'That code did not work. Check it, or ask for a fresh one.',
            weak: 'That password is too easy to guess or has appeared in a data breach. Choose another.',
            short: 'Use at least 8 characters.',
            email: 'Enter an email address.',
        },
    },
    // Google, Discord and Steam: the buttons, and what a refused round trip comes back with (`?error=`).
    social: {
        or: 'or',
        groupLabel: 'Continue with',
        provider: {
            google: 'Google',
            discord: 'Discord',
            steam: 'Steam',
        },
        continue: {
            google: 'Continue with Google',
            discord: 'Continue with Discord',
            steam: 'Continue with Steam',
        },
        start: 'We could not reach {{provider}}. Try again shortly.',
        error: {
            noAccount: 'There is no account for that {{provider}} account yet. Create one first: it takes a moment.',
            agreeFirst: 'There is no account for that {{provider}} account yet. Agree to the terms below, then continue with {{provider}} again.',
            notLinked: 'This email already has a Clavian account. Sign in with your password, then link {{provider}} from your account settings.',
            cancelled: 'You cancelled signing in with {{provider}}. Nothing was changed.',
            expired: 'That sign-in took too long or was started in another browser. Start again.',
            refused: 'An account cannot be created with this email address.',
            takenByOther: 'That {{provider}} account is already linked to another Clavian account.',
            unverified: '{{provider}} has not confirmed an email address for that account, so it cannot be linked. Confirm your email with {{provider}}, then try again.',
            generic: 'Signing in with {{provider}} did not work. Try again, or sign in another way.',
        },
        signUpInstead: 'Create your account',
    },
    // Creating an account (P-03 on the web): with an email and a password, or with Google, Discord or Steam. The terms
    // are agreed before any account is made.
    signUp: {
        title: 'Create your account',
        intro: 'One account for every venue on Clavian.',
        introSocial: 'Use an account you already have. If you are new here, we ask you to agree to the terms next.',
        dialog: {
            title: 'Create your Clavian account',
            body: 'You are new here. Agree to the terms to create your account with {{provider}}.',
            confirm: 'Continue with {{provider}}',
            cancel: 'Not now',
        },
        form: {
            name: 'Your name',
            nameHint: 'How venues greet you. You can change it later.',
            email: 'Email',
            password: 'Password',
            passwordHint: 'At least 8 characters.',
            submit: 'Create account',
            or: 'or',
        },
        consent: {
            label: 'I agree to the Terms of Service and the Privacy Policy',
            labelLinked: 'I agree to the {{terms}} and the {{privacy}}',
            terms: 'Terms of Service',
            privacy: 'Privacy Policy',
            required: 'Agree to the terms to continue.',
            marketing: 'Send me offers and news by email',
        },
        failure: {
            weak: 'That password is too easy to guess or has appeared in a data breach. Choose another.',
            short: 'Use at least 8 characters.',
            email: 'Enter an email address we can send a code to.',
            outdated: 'The terms changed while you were signing up. Reload the page and agree to the new version.',
        },
        legalUnavailable: 'Creating an account is not available here right now. Ask at a venue.',
        noProviders: 'Creating an account with another service is not available right now.',
        footer: 'You can also create your account at any venue PC.',
        signInInstead: 'Already have an account? Sign in',
    },
    // "Complete your profile": right after a Google, Discord or Steam sign-up, before anything else.
    welcome: {
        title: 'Complete your profile',
        intro: 'Choose how venues and friends see you. You can change both later.',
        username: 'Gamer tag',
        usernameHint: '3 to 20 letters, digits, "_" or ".".',
        usernameInvalid: 'Use 3 to 20 letters, digits, "_" or ".".',
        usernameTaken: 'That gamer tag is taken. Try one of these, or another of your own.',
        suggestionsLabel: 'Free gamer tags',
        displayName: 'Display name',
        displayNameHint: 'How venues and friends see you.',
        submit: 'Save and continue',
        failed: 'That did not save. Try again.',
    },
    // The page the api sends a refused sign-in to when it cannot tell which screen it came from.
    signInError: {
        title: 'We could not sign you in',
        back: 'Back to sign in',
        home: 'Go to the home page',
        error: {
            cancelled: 'You cancelled signing in. Nothing was changed.',
            expired: 'That sign-in took too long or was started in another browser. Start again from the sign-in page.',
            noAccount: 'There is no account for that sign-in yet. Create your account first, agreeing to the terms.',
            notLinked: 'This email already has a Clavian account. Sign in with your password, then link Google from your account settings.',
            banned: 'This account cannot sign in. Contact the venue.',
            refused: 'An account cannot be created with this email address.',
            takenByOther: 'That account is already linked to another Clavian account.',
            unverified: 'That account has no confirmed email address, so it cannot be linked. Confirm your email with the provider, then try again.',
            generic: 'Something went wrong while signing you in. Start again from the sign-in page.',
        },
    },
    // Finishing a sign-in that started at a venue's PC: opened from the code the PC shows, or in the window the PC
    // opens. The heading names the venue and the PC before anything is signed in, so a code shown out of context is
    // caught by the player.
    stationSignIn: {
        checking: 'Checking this sign-in link',
        review: 'Sign in to {{station}} at {{venue}}',
        confirmTitle: 'Sign in to {{station}} at {{venue}}?',
        expiresIn: 'This link expires in {{time}}.',
        expiresSoon: 'This link only works for a few minutes.',
        signedOut: {
            body: 'Sign in or create your account to start playing on this PC.',
            signIn: 'Sign in',
            // Under the Google, Discord and Steam buttons: the sign-in page's other ways (password, phone code).
            signInOtherWay: 'Sign in another way',
            createAccount: 'New here? Create your account',
        },
        confirm: {
            signedInAs: 'Signed in as {{name}}',
            onlyHere: 'Only continue if you are at this PC right now.',
            start: 'Start playing on {{station}}',
            cancel: 'This isn’t my PC',
            switchAccount: 'Use a different account',
        },
        // Opened by the PC's own sign-in window for a provider the player tapped there: straight to that provider, then
        // the PC signs in by itself. Only a refusal stops on this page, in words, with a way to try again.
        window: {
            redirecting: 'Taking you to {{provider}}…',
            signingIn: 'Signing you in on {{station}}…',
            retryTitle: 'Sign in with {{provider}}',
        },
        done: {
            title: 'You’re signed in on {{station}}',
            body: 'You can put your phone away and head to the PC.',
        },
        cancelled: {
            title: 'Nothing was signed in',
            body: 'You can close this page.',
        },
        failure: {
            missingTitle: 'No sign-in link here',
            invalidTitle: 'This link doesn’t work',
            missing: 'Open this page by scanning the code on the PC.',
            unavailableTitle: 'This link has expired',
            unavailable: 'This sign-in link has expired or was already used. Start again at the PC to get a new one.',
            bannedTitle: 'You can’t sign in here',
            banned: 'Please see the front desk.',
            signUpDisabledTitle: 'Use an account you already have',
            signUpDisabled: 'This venue doesn’t create accounts at its PCs. Sign in with an account you already have, or ask at the front desk.',
            notOnSiteTitle: 'Connect to the venue’s Wi-Fi',
            notOnSite: 'This venue asks you to sign in from its Wi-Fi. Connect to the venue’s Wi-Fi and try again, or use the PC itself.',
            rateLimitedTitle: 'Too many tries',
            rateLimited: 'Too many tries. Wait a minute and try again.',
            networkTitle: 'Couldn’t reach Clavian',
            network: 'Couldn’t reach Clavian. Check your connection and try again.',
            genericTitle: 'Something went wrong',
            generic: 'Something went wrong on our side. Try again in a moment.',
            retry: 'Try again',
            signOut: 'We couldn’t sign you out. Try again.',
        },
        // "Not you? End it": the link sent after a sign-in at a PC. Public; one press.
        end: {
            title: 'End the session on this PC?',
            body: 'If someone else signed in with your account, ending the session signs them out of that PC straight away.',
            action: 'End it now',
            doneTitle: 'Done',
            done: 'The session on {{station}} at {{venue}} has ended. If this wasn’t you, change your password.',
            nothingTitle: 'Nothing to end',
            nothing: 'There’s no session to end on {{station}} at {{venue}}.',
            expiredTitle: 'This link has expired.',
            expired: 'Links to end a session work for 24 hours after the sign-in. If someone is still using your account at a PC, ask the venue’s staff.',
            missingTitle: 'No link here',
            missing: 'Open this page from the link you were sent after signing in at a PC.',
        },
    },
    // Linking Google, Discord or Steam from a PC during a session: the window the PC opens goes straight to the provider,
    // then comes back here once. The PC closes the window itself.
    stationLink: {
        provider: { google: 'Google', discord: 'Discord', steam: 'Steam' },
        opening: 'Opening {{provider}}…',
        openingAny: 'Opening sign-in…',
        checking: 'Checking this link',
        // Shown anywhere but the PC's own window: what would be linked, to whom, before anything starts.
        confirm: {
            title: 'Link {{provider}} to {{account}} at {{station}}?',
            body: 'This links your own {{provider}} account to the Clavian account {{account}}, for the player at {{station}}, {{venue}}.',
            onlyYours: 'Only continue if {{account}} is your Clavian account. Whoever holds it could then sign in with your {{provider}}.',
            continue: 'Continue',
            cancel: 'Cancel',
        },
        linkedTitle: '{{provider}} is linked',
        linkedTitleAny: 'Linked',
        linked: 'You can sign in with it next time. This window closes by itself.',
        failure: {
            takenTitle: 'Already linked to another account',
            taken: 'This {{provider}} account is linked to a different Clavian account. Try another {{provider}} account at the PC.',
            unverifiedTitle: 'Verify your email first',
            unverified: 'Discord says the email on this account isn’t verified. Verify it in Discord, then try again at the PC.',
            cancelledTitle: 'Nothing was linked',
            cancelled: 'You can close this window.',
            expiredTitle: 'This link has expired',
            expired: 'Start again from Connected accounts on the PC.',
            missingTitle: 'Nothing to link here',
            missing: 'This page opens from Connected accounts on a PC.',
            genericTitle: 'That didn’t work',
            generic: 'Nothing was linked. Try again from Connected accounts on the PC.',
            network: 'Couldn’t reach Clavian. Try again from the PC.',
        },
    },
    home: {
        greeting: 'Hi, {{name}}',
        greetingPlain: 'Welcome back',
        intro: 'Here is your play at a glance.',
        live: {
            title: 'Playing now',
            on: '{{station}} at {{venue}}',
            left: 'left',
            open: 'Open session',
            lock: 'Lock PC',
            unlock: 'Unlock PC',
            locked: 'Locked',
            grace: 'Time is up',
        },
        notPlaying: {
            title: 'Not playing right now',
            body: 'Sign in at any PC in a Clavian venue and your session shows up here, with the controls.',
            find: 'Find a venue',
        },
        wallets: {
            title: 'Wallet',
            none: 'No wallet yet',
            noneBody: 'Your wallet starts with your first top-up at a venue counter.',
            all: 'See wallet',
        },
        passes: {
            title: 'Passes',
            none: 'No passes right now',
            noneBody: 'Passes you buy at a venue appear here with the time left.',
            all: 'See passes',
            left: '{{duration}} left',
        },
        activity: {
            title: 'Recent activity',
            empty: 'Nothing here yet',
            emptyBody: 'Your visits and orders will appear here after your first visit.',
            visit: 'Played {{duration}} on {{station}}',
            order: 'Order at {{venue}}',
            allOrders: 'All orders',
            allVisits: 'All visits',
        },
    },
    // The link in the "account moved" email: what was moved from the café's previous system, and claiming it.
    moved: {
        title: 'Your account at {{venue}} has moved',
        titleNoVenue: 'Your account has moved',
        lead: 'The café moved to Clavian. This is what you had in its previous system.',
        money: 'Money',
        moneyTaxPaid: 'Of it, GST already paid',
        time: 'Play time',
        timeUntil: 'until {{date}}',
        claimBy: 'Claim by',
        nothing: 'No money or time was waiting on this account.',
        howTitle: 'Claim it',
        how: 'Sign in, or create a free account, with the email this message went to: {{email}}. Then come back to this page.',
        signIn: 'Sign in to claim',
        claim: 'Add it to my account',
        claimed: 'Added to your account. You can use it at {{venue}} from your next visit.',
        alreadyClaimed: 'This has already been added to an account.',
        closed: 'This can no longer be claimed here.',
        counterOnly: 'This one is claimed at the front desk of {{venue}}. Bring photo ID, and if you are under 18, a parent or guardian.',
        mismatch: 'You are signed in with another email. Sign in with {{email}}, verify it, and open the link again.',
        invalid: 'This link does not work. It may be old, or it may have been changed. Use the newest email from the café, or ask at the front desk.',
        failed: 'It could not be added right now. Try again, or ask at the front desk.',
        loading: 'Loading what was moved',
        wallet: 'Go to my wallet',
    },
    venues: {
        title: 'Venues',
        subtitle: 'Find a place to play',
        searchLabel: 'Search venues',
        searchPlaceholder: 'Search by name or area',
        openNow: 'Open now',
        count_one: '{{count}} venue',
        count_other: '{{count}} venues',
        filter: {
            legend: 'Show',
            all: 'All',
            open: 'Open now',
        },
        empty: 'No venues match',
        emptyBody: 'Try another name or area, or show every venue.',
        showAll: 'Show all venues',
        loadError: 'We could not load the venues',
        loadErrorBody: 'Something went wrong reaching the venue directory. Try again.',
        openLabel: 'Open now',
        closedLabel: 'Closed',
        distance: '{{km}} km away',
        amenities: {
            parking: 'Parking',
            air_conditioning: 'Air conditioning',
            food: 'Food',
            washrooms: 'Washrooms',
            streaming_room: 'Streaming room',
        },
        view: 'View venue',
    },
    venue: {
        back: 'All venues',
        openNow: 'Open now',
        closed: 'Closed',
        about: 'About',
        hours: 'Opening hours',
        amenities: 'What is here',
        contact: 'Contact',
        website: 'Website',
        phone: 'Call',
        email: 'Email',
        directions: 'Directions',
        notFound: 'That venue is not here',
        notFoundBody: 'It may have moved or closed. Browse the other venues.',
        days: {
            mon: 'Monday',
            tue: 'Tuesday',
            wed: 'Wednesday',
            thu: 'Thursday',
            fri: 'Friday',
            sat: 'Saturday',
            sun: 'Sunday',
        },
        stats: {
            free: 'PCs free now',
            of: 'of {{total}}',
            passes: 'Passes on sale',
            rate: 'From, an hour',
        },
        dayClosed: 'Closed',
        allDay: 'Open all day',
        pcs: {
            title: 'PCs',
            free: '{{free}} of {{total}} free now',
            rate: '{{amount}} an hour',
            passesOnly: 'Play with a pass',
            none: 'This venue has not listed its PCs yet.',
            note: 'Free now means ready for a new player at this moment. It can change by the time you arrive.',
        },
        passes: {
            title: 'Passes',
            none: 'No passes on sale right now. Ask at the counter about play time.',
            play: '{{duration}} of play',
            window: 'When: {{window}}',
            validity: '{{validity}}',
            for: 'For {{classes}}',
        },
        pay: {
            title: 'How to pay',
            body: 'Buy passes and top up your wallet at the venue counter. Pay with cash, card or UPI there.',
            walletBody: 'A pass with a Buy button is paid from your wallet here. Top up your wallet at the venue counter with cash, card or UPI.',
        },
    },
    /** Buying a pass from the wallet at a venue: on the venue page and on Play. */
    shop: {
        title: 'Buy a pass',
        wallet: 'Your wallet here: {{amount}}',
        buy: 'Buy',
        buyNamed: 'Buy {{name}}',
        topUp: 'Top up at the counter',
        none: 'No passes to buy here right now. Ask at the counter about play time.',
        walletOff: 'This venue does not sell passes from your wallet. Buy them at the counter.',
        forYourPc: 'Adds to your session',
        notForYourPc: 'Not for this PC. It waits in your passes.',
        confirm: {
            title: 'Buy {{name}}?',
            body: '{{amount}} from your wallet at {{venue}}. You will have {{left}} left.',
            pay: 'Pay {{amount}}',
            cancel: 'Cancel',
            bodyOnline: '{{amount}} paid online to {{venue}}, with UPI or a card. The pass is added once the payment is confirmed.',
            payOnline: 'Pay {{amount}} online',
        },
        pay: {
            legend: 'How do you want to pay?',
            wallet: 'From your wallet here',
            walletBalance: 'Balance {{amount}}',
            walletShort: 'Not enough in your wallet',
            online: 'Online (UPI or card)',
        },
        done: '{{name}} bought. It is in your passes.',
        doneSession: '{{name}} bought and added to your session.',
        failure: {
            short: 'Your wallet here holds too little for this pass. Top it up at the counter.',
            walletOff: 'This venue does not sell passes from your wallet. Buy them at the counter.',
            priceChanged: 'The price of this pass changed. Check it and buy again.',
            notBuyable: 'This pass cannot be bought here right now. Ask at the counter.',
            generic: 'The pass was not bought. Try again.',
            onlineOff: 'This venue is not taking online payments right now. Buy the pass at the counter.',
            providerDown: 'The payment page is not available right now. Try again in a moment.',
        },
    },
    /** Topping the wallet up online, on a venue's page. */
    onlineTopUp: {
        title: 'Top up your wallet here',
        body: 'Pay online with UPI or a card. The money is added to your wallet once the payment is confirmed.',
        amounts: 'Amount',
        extra: '+{{amount}} extra',
        other: 'Other amount',
        otherHint: 'A whole amount, from {{min}} to {{max}}.',
        notWhole: 'Enter a whole amount, for example 250.',
        tooLow: 'The least you can add is {{min}}.',
        tooHigh: 'The most you can add at once is {{max}}.',
        choose: 'Choose an amount',
        pay: 'Pay {{amount}} online',
        failure: {
            onlineOff: 'This venue is not taking online payments right now. Top up at the counter.',
            walletOff: 'This venue does not use wallets. Ask at the counter.',
            limit: 'That amount is outside what this wallet can hold. Try a different amount.',
            providerDown: 'The payment page is not available right now. Try again in a moment.',
            generic: 'We could not start the payment. Try again.',
        },
    },
    /** Where the payment page sends the player back to. */
    paymentComplete: {
        pendingTitle: 'Confirming your payment',
        pendingBody: 'Keep this page open. It usually takes a few seconds.',
        topUpTitle: 'Wallet topped up',
        topUpDone: '{{amount}} added to your wallet at {{organization}}.',
        topUpExtra: 'Any extra the café offers is added too.',
        passTitle: 'Your pass is ready',
        passDone: 'Your pass {{name}} is ready.',
        failedTitle: 'Payment not completed',
        failed: 'The payment did not go through. You have not been charged.',
        slowTitle: 'Still confirming',
        slow: 'This is taking longer than usual. We will update your wallet or passes as soon as the payment is confirmed.',
        unreadTitle: 'We could not check your payment',
        unread: 'We could not reach the payment status just now. We will update your wallet or passes as soon as the payment is confirmed.',
        thanksTitle: 'Thank you',
        thanks: 'Your payment is being confirmed. You can go back to the PC or the counter.',
        thanksSignIn: 'Sign in to see your wallet and passes.',
        signIn: 'Sign in',
        seePasses: 'See my passes',
        seeWallet: 'See my wallet',
        backToVenue: 'Back to the venue page',
        home: 'Go home',
    },
    // Play: the live session and its controls (P-10), and the visit history.
    play: {
        title: 'Play',
        subtitle: 'Your live session and recent visits',
        live: {
            on: '{{station}} at {{venue}}',
            timeLeft: 'Time left',
            endsAt: 'Runs out at {{time}}',
            noEnd: 'Nothing is paying for more time',
            graceTitle: 'Your time is up',
            graceBody: 'The PC ends the session at {{time}} unless you add time.',
            lockedTitle: 'Your PC is locked',
            lockedBody: 'Your games keep running behind the lock screen.',
            status: {
                active: 'Playing',
                locked: 'Locked',
                grace: 'Time is up',
                starting: 'Starting',
                paused: 'Paused',
                ended: 'Ended',
            },
            updating: 'Updating',
        },
        funding: {
            title: 'What is paying',
            comp: 'Free time from the venue',
            pass: '{{name}}',
            wallet: 'Your wallet',
            postpaid: 'Added time, pay at the counter',
            inUse: 'Now',
            then: 'Then {{minutes}} minutes of grace',
            thenEnd: 'Then the session ends',
            useNext: 'Use next',
            usedNext: '{{name}} is up next.',
        },
        actions: {
            title: 'Controls',
            lock: 'Lock PC',
            unlock: 'Unlock PC',
            locked: 'Your PC is locked.',
            unlocked: 'Your PC is unlocked.',
            lockAsked: 'Asking your PC to lock.',
            unlockAsked: 'Asking your PC to unlock.',
            locking: 'Locking your PC…',
            unlocking: 'Unlocking your PC…',
            lockFailed: 'Your PC did not lock. It may be off or offline. Try again, or ask at the counter.',
            unlockFailed: 'Your PC did not unlock. It may be off or offline. Try again, or ask at the counter.',
            callStaff: 'Call staff',
            end: 'Log out',
        },
        lock: {
            title: 'Lock your PC?',
            body: 'The screen locks and your games keep running. Unlock it here, or at the PC.',
            pin: 'PIN for the PC (optional)',
            pinHint: '4 to 6 digits. You can type it at the PC to unlock. Leave it empty to unlock with your account.',
            confirm: 'Lock PC',
            cancel: 'Cancel',
            pinInvalid: 'Use 4 to 6 digits.',
        },
        call: {
            title: 'Call staff to your PC',
            body: 'Someone from the venue comes to {{station}}.',
            category: 'What is it about?',
            categories: {
                pc: 'The PC',
                peripherals: 'Headset, mouse or keyboard',
                food: 'Food and drinks',
                other: 'Something else',
            },
            note: 'Anything to add? (optional)',
            noteHint: 'Up to 140 characters.',
            send: 'Call staff',
            cancel: 'Not now',
            sent: 'Staff have been called.',
            open: 'Staff have been called',
            acknowledged: '{{name}} is on the way',
            acknowledgedNoName: 'Staff are on the way',
            cancelCall: 'Cancel the call',
            cancelled: 'Your call was cancelled.',
        },
        end: {
            title: 'Log out of {{station}}?',
            body: 'Your session ends now and the PC locks. Anything added on account is paid at the counter.',
            confirm: 'Log out',
            cancel: 'Keep playing',
            done: 'You are logged out of {{station}}.',
        },
        none: {
            title: 'You are not playing right now',
            body: 'Sign in at any PC in a Clavian venue. Your session, its time and its controls appear here.',
            find: 'Find a venue',
        },
        history: {
            title: 'Recent visits',
            empty: 'No visits yet',
            emptyBody: 'Each time you play, the visit appears here.',
            played: 'Played {{duration}}',
            more: 'Show more',
            inProgress: 'Playing now',
            ended: {
                logout: 'You logged out',
                ended_by_player: 'You logged out from your phone',
                time_up: 'Time ran out',
                grace_expired: 'Time ran out',
                staff_ended: 'Ended by staff',
                other: 'Ended',
            },
        },
        failure: {
            generic: 'That did not work. Try again.',
            ended: 'This session has already ended.',
            gone: 'You are not playing any more.',
            staffRun: 'This session is run by staff. Ask at the counter.',
        },
    },
    // Your money: a wallet per venue brand, and its statement (P-15).
    wallet: {
        title: 'Wallet',
        subtitle: 'Your balance at each venue',
        total: 'Balance',
        cash: 'Your money',
        bonus: 'Bonus',
        bonusHint: 'Bonus is spent first and cannot be refunded.',
        usableAt: 'Spend it at any {{organization}} venue.',
        useForPlay: 'Pays for play time when other time runs out',
        useForPlayOff: 'Not used for play time',
        statement: 'Statement',
        statementFor: 'Statement at {{organization}}',
        statementEmpty: 'No movements yet',
        statementEmptyBody: 'Top-ups and spends at {{organization}} will appear here.',
        more: 'Show more',
        kind: {
            topup: 'Top-up',
            topup_bonus: 'Bonus',
            spend: 'Spent',
            adjustment: 'Adjustment',
            refund: 'Refund to you',
            sale_refund: 'Refund to wallet',
            carried_over: 'Moved from the old system',
        },
        none: 'No wallet yet',
        noneBody: 'Your wallet starts with your first top-up at a venue counter. Then you can spend it on play, passes and food.',
        findVenue: 'Find a venue',
        topUp: {
            title: 'Top up',
            body: 'Top up at the counter, or online from a café’s page where it takes online payments.',
            bodyNone: 'Top up at a venue counter, with cash, card or UPI.',
        },
    },
    passes: {
        title: 'Passes',
        subtitle: 'Play time you have bought',
        active: 'Active',
        past: 'Used and expired',
        left: '{{duration}} left',
        of: 'of {{duration}}',
        expires: 'Expires {{date}}',
        expiresAfterFirstUse: 'The clock starts the first time you play on it',
        usableAt: 'At {{venues}}',
        usableEverywhere: 'At every {{organization}} venue',
        place: {
            in_use: 'In use now',
            queued: 'Up next',
        },
        status: {
            active: 'Active',
            exhausted: 'Used up',
            expired: 'Expired',
            refunded: 'Refunded',
            voided: 'Cancelled',
        },
        none: 'No passes right now',
        noneBody: 'Passes are sold at the venue counter. Each venue lists its passes and prices on its page.',
        findVenue: 'See venues and prices',
        paid: 'Paid {{amount}}',
        free: 'From the venue',
        claimGuest: 'Claim a guest pass',
    },
    // Claim a guest pass: a walk-in's ticket code from a counter receipt, moved into this account.
    guestPass: {
        title: 'Claim a guest pass',
        subtitle: 'Played as a guest? Add your ticket’s time and visits to your account.',
        back: 'Back to passes',
        formTitle: 'Your ticket code',
        code: 'Ticket code',
        codeHint: 'The six characters on your receipt. Dashes and spaces are fine.',
        claim: 'Claim',
        note: 'You can claim a ticket for {{days}} days after it was bought. Once it is claimed, the code no longer starts play at a PC.',
        required: 'Enter the code from your receipt.',
        malformed: 'A ticket code is six characters, without 0, O, 1 or I.',
        invalid: 'That ticket code is not right. Check it against your receipt and try again.',
        unavailable: 'This ticket can’t be claimed. It may already have been claimed, it may be more than {{days}} days old, or someone may still be playing on it at a PC. Ask at the venue’s front desk if you are not sure.',
        rateLimited: 'Too many tries. Wait a few minutes, then try again.',
        failed: 'That did not work. Try again, or ask at the venue’s front desk.',
        claimedToast: 'Ticket claimed',
        doneTitle: 'Added to your account',
        doneTime: '{{duration}} of {{pass}} at {{venue}} is now on your account.',
        doneNoTime: '{{pass}} at {{venue}} is now on your account. It has no time left.',
        doneUntil: 'Use it until {{date}}.',
        doneSessions_one: '{{count}} session you played as a guest is now in your history.',
        doneSessions_other: '{{count}} sessions you played as a guest are now in your history.',
        toPasses: 'See my passes',
    },
    orders: {
        title: 'Orders',
        subtitle: 'Everything you bought, with receipts',
        none: 'No orders yet',
        noneBody: 'Orders you place at a venue counter or from a PC appear here with their receipts.',
        findVenue: 'Find a venue',
        more: 'Show more',
        number: 'Order #{{number}}',
        items_one: '{{count}} item',
        items_other: '{{count}} items',
        status: {
            placed: 'Pay at the counter',
            partially_paid: 'Part paid',
            paid: 'Paid',
            partially_refunded: 'Part refunded',
            refunded: 'Refunded',
            cancelled: 'Cancelled',
        },
        detail: {
            back: 'All orders',
            title: 'Receipt',
            bill: 'Bill',
            bill_of_supply: 'Bill of supply',
            tax_invoice: 'Tax invoice',
            number: 'Number',
            order: 'Order',
            date: 'Date',
            venue: 'Venue',
            gstin: 'GSTIN',
            items: 'Items',
            each: '{{amount}} each',
            subtotal: 'Subtotal',
            promotion: '{{name}}',
            discountCode: 'Code {{code}}',
            discountManual: 'Discount',
            tax: 'Tax included',
            taxLine: '{{component}} {{rate}}%',
            total: 'Total',
            paid: 'Payment',
            tender: {
                cash: 'Cash',
                counter_card: 'Card',
                counter_upi: 'UPI',
                wallet: 'Wallet',
                on_account: 'On account',
                online: 'Online (UPI/card)',
            },
            cashReceived: 'Cash received',
            change: 'Change',
            changeToWallet: 'Change added to your wallet',
            refunds: 'Refunds',
            refund: 'Refund to {{tender}}',
            refundPending: 'Waiting for approval',
            outstanding: 'Still to pay',
            payAtCounter: 'Pay this at the venue counter.',
            noDocument: 'The receipt is issued once the order is paid.',
            notFound: 'That order is not here',
            notFoundBody: 'It may belong to another account. Your orders are listed on the Orders page.',
        },
    },
    account: {
        title: 'Account',
        subtitle: 'Profile, security and notifications',
        sections: 'Account sections',
        profile: {
            title: 'Profile',
            name: 'Display name',
            nameHint: 'How venues and friends see you.',
            username: 'Gamer tag',
            notSet: 'Not set',
            edit: 'Edit name',
            save: 'Save name',
            cancel: 'Cancel',
            saved: 'Saved.',
            saveError: 'That did not save. Try again.',
        },
        email: {
            title: 'Email',
            none: 'No email on this account',
            verified: 'Verified',
            unverified: 'Not verified',
            change: 'Change email',
            add: 'Add email',
            newEmail: 'New email',
            send: 'Send a code',
            sentTo: 'We sent a code to {{destination}}. Enter it to confirm the change.',
            code: 'The code',
            confirm: 'Confirm new email',
            cancel: 'Cancel',
            changed: 'Your email is changed. We let the old address know.',
            fresh: 'For your security, sign in again before you change your email, then come back here.',
            inUse: 'That email is already on another account.',
            invalid: 'Enter an email address.',
            failed: 'That did not work. Try again.',
        },
        password: {
            title: 'Password',
            body: 'We email a code to {{email}}, then you choose a new password.',
            noEmail: 'Add an email first: the code to set a password goes there.',
            change: 'Change password',
            set: 'Set a password',
        },
        twoFactor: {
            title: 'Two-factor authentication',
            on: 'On',
            off: 'Off',
            bodyOff: 'Ask for a code from an authenticator app each time you sign in with your password.',
            bodyOn: 'Signing in with your password also asks for a code from your authenticator app.',
            needsPassword: 'Set a password first. Two-factor protects password sign-in.',
            turnOn: 'Turn on',
            turnOff: 'Turn off',
            newCodes: 'New backup codes',
            password: 'Your password',
            passwordHint: 'To confirm it is you.',
            start: 'Continue',
            cancel: 'Cancel',
            setupTitle: 'Set up your authenticator',
            scan: 'Scan this code with your authenticator app (Google Authenticator, Microsoft Authenticator, 1Password or similar).',
            qrLabel: 'QR code to add Clavian to your authenticator app',
            key: 'Or enter this key',
            openLink: 'Open in authenticator app',
            backupTitle: 'Save your backup codes',
            backupBody: 'Each code works once if you lose your phone. Keep them somewhere safe. They are shown only now.',
            copy: 'Copy codes',
            copied: 'Codes copied.',
            code: 'Code from the app',
            verify: 'Verify and turn on',
            enabled: 'Two-factor authentication is on.',
            disabled: 'Two-factor authentication is off.',
            offTitle: 'Turn off two-factor?',
            offBody: 'Signing in will only need your password.',
            newCodesTitle: 'New backup codes',
            newCodesBody: 'Your old backup codes stop working.',
            fresh: 'For your security, sign in again, then come back here.',
            failed: 'That did not work. Check your password and try again.',
            wrongCode: 'That code did not work. Check the time on your phone and try again.',
            done: 'Done',
        },
        devices: {
            title: 'Where you are signed in',
            empty: 'Only this device',
            current: 'This device',
            signOut: 'Sign out',
            signOutLabel: 'Sign out of {{device}}',
            lastActive: 'Last active {{time}}',
            unknown: 'A browser',
        },
        notifications: {
            title: 'Notifications',
            intro: 'What we send you, and where.',
            marketing: 'Offers and news by email',
            marketingHint: 'Off unless you turn it on. You can turn it off at any time.',
            security: 'Security alerts',
            securityHint: 'Sign-ins and account changes, by email. Always on, to keep your account safe.',
            receipts: 'Receipts',
            receiptsHint: 'Sent by email when a venue sends one. Every receipt is also on your Orders page.',
            alwaysOn: 'Always on',
            saved: 'Saved.',
            saveError: 'That did not save. Try again.',
        },
        legal: {
            title: 'Terms and privacy',
            accepted: 'You agreed to the Terms of Service and the Privacy Policy.',
            read: 'Read the {{document}}',
            terms: 'Terms of Service',
            privacy: 'Privacy Policy',
            updatedTitle: 'Our terms have changed',
            updatedBody: 'Please read and agree to the updated {{documents}} to keep using your account.',
            updatedBoth: 'Terms of Service and Privacy Policy',
            agree: 'I agree',
            agreed: 'Thanks. You agreed to the updated terms.',
            later: 'Later',
        },
        appearance: {
            title: 'Appearance',
            label: 'Theme',
        },
        connected: {
            title: 'Connected accounts',
            hint: 'Sign in with Google, Discord or Steam as well. You can unlink one while you still have another way to sign in.',
            listLabel: 'Connected accounts',
            linked: 'Linked',
            notLinked: 'Not linked',
            linkedOn: 'Linked {{date}}',
            link: 'Link {{provider}}',
            unlink: 'Unlink',
            unlinkLabel: 'Unlink {{provider}}',
            onlyWay: 'Your only way to sign in. Link another account or set a password first.',
            unavailable: 'Your connected accounts could not be loaded.',
            retry: 'Try again',
            confirm: {
                title: 'Unlink {{provider}}?',
                body: 'You will not be able to sign in with {{provider}} any more. You can link it again at any time.',
                confirm: 'Unlink {{provider}}',
                cancel: 'Keep it',
            },
            linkedToast: '{{provider}} is linked. You can sign in with it now.',
            unlinkedToast: '{{provider}} is unlinked.',
            stale: 'For your security, sign in again before you unlink {{provider}}, then come back here.',
            signInAgain: 'Sign in again',
            lastWay: '{{provider}} is your only way to sign in, so it cannot be unlinked yet.',
            linkFailed: 'Linking {{provider}} did not work. Try again.',
            unlinkFailed: 'Unlinking {{provider}} did not work. Try again.',
            thatProvider: 'your account',
            error: {
                cancelled: 'You cancelled linking {{provider}}. Nothing was changed.',
                expired: 'That took too long or was started in another browser. Start again.',
                takenByOther: 'That {{provider}} account is already linked to another Clavian account.',
                unverified: '{{provider}} has not confirmed an email address for that account, so it cannot be linked. Confirm your email with {{provider}}, then try again.',
                generic: 'Linking {{provider}} did not work. Try again.',
            },
        },
        signInAgain: 'Sign in again',
        signOut: 'Sign out',
        signOutError: 'We could not sign you out. Try again.',
    },
    errors: {
        generic: 'Something went wrong. Try again.',
        notFoundTitle: 'Nothing here',
        notFoundBody: 'That page does not exist. Head back home.',
        notFoundHome: 'Go home',
        loadTitle: 'We could not load this',
        loadBody: 'Something went wrong. Check your connection and try again.',
        retry: 'Try again',
    },
};
export default playerWeb;
//# sourceMappingURL=player-web.js.map